import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/AuthContext";
import { PageHeader, Card, Modal, Field, Btn, Pill, Empty, inputCls } from "@/dashboard/ui";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Ban, CalendarClock, Pencil, Link2 } from "lucide-react";
import { StaffPanel, ServicesPanel, BookingSettingsPanel } from "@/dashboard/AppointmentsAdmin";
import PhoneInput from "@/components/PhoneInput";

const TABS = [
  { id: "calendar", label: "Calendar" },
  { id: "list", label: "Appointments" },
  { id: "staff", label: "Staff" },
  { id: "services", label: "Services" },
  { id: "settings", label: "Booking Settings" },
];

const STATUS_TONE = { Confirmed: "black", Cancelled: "red", Completed: "grey", "No-show": "outline" };

const fmtTime = (iso, tz) => new Date(iso).toLocaleTimeString("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit" });
const dayKey = (iso, tz) => new Date(iso).toLocaleDateString("en-CA", { timeZone: tz });
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDayHeading = (d) => d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

function SlotPicker({ serviceId, staffId, dateStr, excludeId, tz, onPick, selected }) {
  const [state, setState] = useState({ loading: false, slots: null, error: "" });

  useEffect(() => {
    if (!serviceId || !dateStr) {
      setState({ loading: false, slots: null, error: "" });
      return;
    }
    let on = true;
    setState({ loading: true, slots: null, error: "" });
    api
      .get("/availability", { params: { service_id: serviceId, date: dateStr, staff_id: staffId || "", exclude_appointment_id: excludeId || "" } })
      .then((r) => on && setState({ loading: false, slots: r.data.slots, error: "" }))
      .catch((e) => on && setState({ loading: false, slots: null, error: formatApiError(e) }));
    return () => { on = false; };
  }, [serviceId, staffId, dateStr, excludeId]);

  if (!serviceId || !dateStr) return <p className="text-xs text-neutral-400">Choose a service and date to see available times.</p>;
  if (state.loading) return <p className="text-xs text-neutral-400">Checking availability…</p>;
  if (state.error) return <p className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600" data-testid="slots-error">{state.error}</p>;
  if (!state.slots?.length) return <p className="text-xs text-neutral-500" data-testid="slots-empty">No free times on this date — try another day or staff member.</p>;
  return (
    <div className="grid max-h-44 grid-cols-3 gap-1.5 overflow-y-auto sm:grid-cols-4" data-testid="slot-grid">
      {state.slots.map((s, i) => {
        const on = selected === s.start + s.staff_id;
        return (
          <button
            type="button"
            key={s.start + s.staff_id}
            data-testid={`slot-option-${i}`}
            onClick={() => onPick(s)}
            className={`rounded-lg border px-2 py-1.5 text-xs font-semibold transition-colors ${
              on ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-200 text-neutral-600 hover:border-[#111111]"
            }`}
          >
            {fmtTime(s.start, tz)}
            {!staffId && <span className="block text-[10px] font-normal opacity-70">{s.staff_name}</span>}
          </button>
        );
      })}
    </div>
  );
}

function BookingModal({ tz, staff, services, leads, onClose, onSaved }) {
  const [leadId, setLeadId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [dateStr, setDateStr] = useState(isoDay(new Date()));
  const [slot, setSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const activeServices = services.filter((s) => s.active);
  const service = services.find((s) => s.id === serviceId);
  const qualifiedStaff = service ? staff.filter((st) => st.active && (service.staff_ids || []).includes(st.id)) : [];
  const missingContact = !customerPhone.trim() && !customerEmail.trim();

  const pickLead = (id) => {
    setLeadId(id);
    const l = leads.find((x) => x.id === id);
    if (l) {
      setCustomerName(l.full_name || "");
      setCustomerPhone(l.phone || "");
      setCustomerEmail(l.email || "");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!slot) {
      setError("Choose an available time slot.");
      return;
    }
    if (missingContact) {
      setError("Add at least one contact method — a phone number or an email address.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/appointments", {
        service_id: serviceId,
        start: slot.start,
        staff_id: staffId || null,
        lead_id: leadId || null,
        customer_name: customerName || null,
        customer_phone: customerPhone || null,
        customer_email: customerEmail || null,
        notes: notes || null,
      });
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal testid="booking-modal" title="New appointment" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="CRM lead (optional — links the appointment)">
          <select data-testid="book-lead-select" className={inputCls} value={leadId} onChange={(e) => pickLead(e.target.value)}>
            <option value="">No CRM link — enter customer manually</option>
            {leads.map((l) => <option key={l.id} value={l.id}>{l.full_name} · {l.phone}</option>)}
          </select>
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Customer name *">
            <input data-testid="book-customer-name" className={inputCls} value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
          </Field>
          <PhoneInput
            testid="book-customer-phone"
            label="Customer phone"
            value={customerPhone}
            onChange={setCustomerPhone}
          />
        </div>
        <Field label="Customer email" hint={missingContact ? "Required if no phone number is given." : "Optional when a phone number is given."}>
          <input data-testid="book-customer-email" type="email" className={inputCls} value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="customer@example.com" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Service *">
            <select data-testid="book-service-select" className={inputCls} value={serviceId} onChange={(e) => { setServiceId(e.target.value); setStaffId(""); setSlot(null); }} required>
              <option value="">Choose a service…</option>
              {activeServices.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.duration_min} min</option>)}
            </select>
          </Field>
          <Field label="Staff member">
            <select data-testid="book-staff-select" className={inputCls} value={staffId} onChange={(e) => { setStaffId(e.target.value); setSlot(null); }}>
              <option value="">Auto-assign available staff</option>
              {qualifiedStaff.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
            </select>
          </Field>
        </div>
        {service && qualifiedStaff.length === 0 && (
          <p className="rounded-xl bg-neutral-50 px-4 py-2.5 text-xs text-neutral-600" data-testid="book-no-staff">
            No active staff are qualified for this service — assign qualified staff in the Services tab first.
          </p>
        )}
        <Field label={`Date (times shown in ${tz})`}>
          <input data-testid="book-date-input" type="date" className={inputCls} value={dateStr} min={isoDay(new Date())} onChange={(e) => { setDateStr(e.target.value); setSlot(null); }} required />
        </Field>
        <Field label="Available time *">
          <SlotPicker serviceId={serviceId} staffId={staffId} dateStr={dateStr} tz={tz} selected={slot ? slot.start + slot.staff_id : null}
            onPick={(s) => setSlot(s)} />
        </Field>
        <Field label="Internal notes">
          <textarea data-testid="book-notes-input" rows={2} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {error && <p data-testid="book-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2.5">
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn data-testid="book-submit-button" type="submit" disabled={busy || !slot || !serviceId || !customerName.trim() || missingContact}
            title={missingContact ? "Add a phone number or email first" : !slot ? "Choose an available time slot" : undefined}>
            {busy ? "Booking…" : "Confirm Booking"}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

function RescheduleModal({ tz, appt, staff, services, onClose, onSaved }) {
  const [dateStr, setDateStr] = useState(dayKey(appt.start, tz));
  const [staffId, setStaffId] = useState(appt.staff_id);
  const [slot, setSlot] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const service = services.find((s) => s.id === appt.service_id);
  const qualifiedStaff = service ? staff.filter((st) => st.active && (service.staff_ids || []).includes(st.id)) : [];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!slot) {
      setError("Choose a new time slot.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/appointments/${appt.id}/reschedule`, { start: slot.start, staff_id: staffId });
      onSaved(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal testid="reschedule-modal" title={`Reschedule — ${appt.customer_name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-xs text-neutral-500">
          Currently {dayKey(appt.start, tz)} at {fmtTime(appt.start, tz)} with {appt.staff_name}. The original slot is only released after the new one is safely booked.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="New date">
            <input data-testid="reschedule-date-input" type="date" className={inputCls} value={dateStr} min={isoDay(new Date())} onChange={(e) => { setDateStr(e.target.value); setSlot(null); }} required />
          </Field>
          <Field label="Staff member">
            <select data-testid="reschedule-staff-select" className={inputCls} value={staffId} onChange={(e) => { setStaffId(e.target.value); setSlot(null); }}>
              {qualifiedStaff.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label={`New time (${tz})`}>
          <SlotPicker serviceId={appt.service_id} staffId={staffId} dateStr={dateStr} excludeId={appt.id} tz={tz}
            selected={slot ? slot.start + slot.staff_id : null} onPick={(s) => setSlot(s)} />
        </Field>
        {error && <p data-testid="reschedule-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2.5">
          <Btn type="button" variant="ghost" onClick={onClose}>Keep Original</Btn>
          <Btn data-testid="reschedule-submit-button" type="submit" disabled={busy || !slot}>{busy ? "Moving…" : "Confirm New Time"}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function EditModal({ appt, leads, onClose, onSaved }) {
  const [customerName, setCustomerName] = useState(appt.customer_name || "");
  const [customerPhone, setCustomerPhone] = useState(appt.customer_phone || "");
  const [customerEmail, setCustomerEmail] = useState(appt.customer_email || "");
  const [leadId, setLeadId] = useState(appt.lead_id || "");
  const [notes, setNotes] = useState(appt.notes || "");
  const [status, setStatus] = useState(appt.status);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const pickLead = (id) => {
    setLeadId(id);
    const l = leads.find((x) => x.id === id);
    if (l) {
      // CRM is authoritative: refresh contact fields with the lead's current values
      setCustomerName(l.full_name || "");
      setCustomerPhone(l.phone || "");
      setCustomerEmail(l.email || "");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.put(`/appointments/${appt.id}`, {
        customer_name: customerName, customer_phone: customerPhone || null, customer_email: customerEmail || null,
        lead_id: leadId || null, notes: notes || null, status,
      });
      onSaved(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal testid="edit-appointment-modal" title={`Edit — ${appt.customer_name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Customer name *">
            <input data-testid="edit-customer-name" className={inputCls} value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
          </Field>
          <PhoneInput
            testid="edit-customer-phone"
            label="Customer phone"
            value={customerPhone}
            onChange={setCustomerPhone}
          />
        </div>
        <Field label="Customer email" hint="At least one contact method (phone or email) is required. Selecting a CRM lead refreshes these with the latest CRM values.">
          <input data-testid="edit-customer-email" type="email" className={inputCls} value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="customer@example.com" />
        </Field>
        <Field label="CRM lead">
          <select data-testid="edit-lead-select" className={inputCls} value={leadId} onChange={(e) => pickLead(e.target.value)}>
            <option value="">No CRM link</option>
            {leads.map((l) => <option key={l.id} value={l.id}>{l.full_name} · {l.phone}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select data-testid="edit-status-select" className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)}>
            {["Confirmed", "Cancelled", "Completed", "No-show"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Internal notes">
          <textarea data-testid="edit-notes-input" rows={2} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {error && <p data-testid="edit-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2.5">
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn data-testid="edit-appointment-save" type="submit" disabled={busy || !customerName.trim() || (!customerPhone.trim() && !customerEmail.trim())}>{busy ? "Saving…" : "Save Changes"}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function DetailModal({ tz, appt: initial, leads, onClose, onChanged, onReschedule, onEdit }) {
  const [doc, setDoc] = useState(initial);
  const [busy, setBusy] = useState(false);

  const refreshDoc = useCallback(async () => {
    try {
      const r = await api.get(`/appointments/${initial.id}`);
      setDoc(r.data);
      onChanged();
    } catch { /* keep last known state */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.id]);

  useEffect(() => {
    refreshDoc();
  }, [refreshDoc]);

  const cancel = async () => {
    if (!window.confirm(`Cancel the appointment for ${doc.customer_name}? The record is kept for your history.`)) return;
    setBusy(true);
    try {
      await api.post(`/appointments/${doc.id}/cancel`);
      await refreshDoc();
    } finally {
      setBusy(false);
    }
  };

  const leadContact = doc.lead_contact || null;
  const leadExists = doc.lead_id && leads.some((l) => l.id === doc.lead_id);
  const [linkCopied, setLinkCopied] = useState(false);

  const copyManageLink = async () => {
    try {
      const { data } = await api.post(`/appointments/${doc.id}/manage-link`);
      const url = `${window.location.origin}${data.manage_path}`;
      await navigator.clipboard?.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch { /* leave uncopied */ }
  };
  return (
    <Modal testid="appointment-detail-modal" title="Appointment details" onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-base font-bold text-[#111111]">{doc.customer_name}</p>
          <Pill tone={STATUS_TONE[doc.status] || "grey"}>{doc.status}</Pill>
        </div>
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div><p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Service</p><p className="mt-0.5 text-neutral-700">{doc.service_name} · {doc.duration_min} min</p></div>
          <div><p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Staff</p><p className="mt-0.5 text-neutral-700">{doc.staff_name}</p></div>
          <div><p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">When</p><p className="mt-0.5 text-neutral-700" data-testid="detail-when">{dayKey(doc.start, tz)} · {fmtTime(doc.start, tz)}–{fmtTime(doc.end, tz)} ({doc.timezone})</p></div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">CRM link</p>
            <p className="mt-0.5 text-neutral-700">
              {leadExists ? (
                <Link to={`/dashboard/leads?search=${encodeURIComponent(doc.customer_name)}`} data-testid="detail-crm-link" className="font-semibold underline underline-offset-2">{doc.customer_name} →</Link>
              ) : doc.lead_id ? (
                <span className="text-neutral-400">Lead removed from CRM (record kept)</span>
              ) : (
                <span className="text-neutral-400">Not linked</span>
              )}
            </p>
          </div>
        </div>

        {leadContact ? (
          <div className="rounded-xl border border-neutral-200 p-3.5" data-testid="detail-crm-contact">
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400">Current CRM contact (authoritative)</p>
            <p className="text-xs text-neutral-700">
              {leadContact.phone || "No phone"}{leadContact.phone && leadContact.email ? " · " : ""}{leadContact.email || ""}
              {!leadContact.phone && !leadContact.email && <span className="text-neutral-400">No contact details on the lead</span>}
            </p>
            {(leadContact.phone !== doc.customer_phone || (leadContact.email || null) !== (doc.customer_email || null)) && (
              <p className="mt-1.5 text-[11px] text-neutral-400">
                Booking snapshot: {doc.customer_phone || "—"}{doc.customer_email ? ` · ${doc.customer_email}` : ""} (recorded at booking time)
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-neutral-500" data-testid="detail-contact">
            {doc.customer_phone || ""}{doc.customer_phone && doc.customer_email ? " · " : ""}{doc.customer_email || ""}
          </p>
        )}

        {doc.notes && <p className="rounded-xl bg-neutral-50 px-4 py-2.5 text-xs text-neutral-600">{doc.notes}</p>}
        {doc.history?.length > 0 && (
          <div>
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400">History</p>
            <ul className="space-y-1" data-testid="detail-history">
              {doc.history.map((h, i) => (
                <li key={i} className="text-[11px] text-neutral-500">{String(h.at).slice(0, 16).replace("T", " ")} — {h.detail}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2.5 border-t border-neutral-100 pt-4">
          <Btn type="button" variant="ghost" data-testid="detail-manage-link-button" onClick={copyManageLink} title="Copy a link your customer can use to reschedule or cancel this booking">
            <Link2 size={14} /> {linkCopied ? "Link copied" : "Customer link"}
          </Btn>
          <Btn type="button" variant="ghost" data-testid="detail-edit-button" onClick={() => onEdit(doc)}><Pencil size={14} /> Edit</Btn>
          {doc.status === "Confirmed" && (
            <>
              <Btn type="button" variant="ghost" data-testid="detail-reschedule-button" onClick={() => onReschedule(doc)}><CalendarClock size={14} /> Reschedule</Btn>
              <Btn type="button" variant="ghost" data-testid="detail-cancel-button" onClick={cancel} disabled={busy}><Ban size={14} /> {busy ? "Cancelling…" : "Cancel Appointment"}</Btn>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

function ApptChip({ a, tz, onClick, conflict }) {
  return (
    <button
      data-testid={`appt-chip-${a.id}`}
      onClick={onClick}
      className={`block w-full truncate rounded-lg px-2 py-1 text-left text-[11px] font-semibold transition-colors ${
        a.status === "Cancelled"
          ? "bg-neutral-100 text-neutral-400 line-through"
          : conflict
            ? "bg-red-600 text-white hover:bg-red-700"
            : "bg-[#111111] text-white hover:bg-neutral-800"
      }`}
      title={`${a.customer_name} · ${a.service_name} · ${a.staff_name} · ${a.status}${conflict ? " · CONFLICT: overlaps another confirmed booking for this staff member" : ""}`}
    >
      {fmtTime(a.start, tz)} {a.customer_name}{conflict ? " •conflict" : ""}
    </button>
  );
}

function readCalPrefs(key) {
  try {
    const p = JSON.parse(localStorage.getItem(key) || "null");
    if (p && ["day", "week", "month"].includes(p.view)) {
      const d = p.cursor ? new Date(`${p.cursor}T12:00:00`) : null;
      if (d && !isNaN(d)) return { view: p.view, cursor: d };
      return { view: p.view, cursor: new Date() };
    }
  } catch { /* ignore corrupt prefs */ }
  return { view: "week", cursor: new Date() };
}

function findOverlapIds(items) {
  const byStaff = {};
  (items || []).forEach((a) => {
    if (a.status === "Confirmed") (byStaff[a.staff_id] = byStaff[a.staff_id] || []).push(a);
  });
  const ids = new Set();
  Object.values(byStaff).forEach((list) => {
    list.sort((x, y) => x.block_start.localeCompare(y.block_start));
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        if (list[j].block_start < list[i].block_end) {
          ids.add(list[i].id);
          ids.add(list[j].id);
        } else break;
      }
    }
  });
  return ids;
}

export default function Appointments() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const tab = TABS.some((t) => t.id === requested) ? requested : "calendar";
  const setTab = (id) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", id); return n; });

  const prefsKey = `sk_appt_cal_${user?.id || "anon"}`;
  const [settings, setSettings] = useState(null);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [leads, setLeads] = useState([]);
  const [appointments, setAppointments] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [view, setView] = useState(() => readCalPrefs(prefsKey).view);
  const [cursor, setCursor] = useState(() => readCalPrefs(prefsKey).cursor);
  const [staffFilter, setStaffFilter] = useState("");
  const [modal, setModal] = useState(null);
  const tz = settings?.timezone || "Europe/London";

  useEffect(() => {
    try {
      localStorage.setItem(prefsKey, JSON.stringify({ view, cursor: isoDay(cursor) }));
    } catch { /* storage unavailable */ }
  }, [prefsKey, view, cursor]);

  const loadCore = useCallback(() => {
    api.get("/booking/settings").then((r) => setSettings(r.data)).catch(() => setSettings({ timezone: "Europe/London" }));
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => setStaff([]));
    api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([]));
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, []);

  useEffect(loadCore, [loadCore]);

  const range = useMemo(() => {
    const d = new Date(cursor);
    if (view === "day") return { from: isoDay(d), to: isoDay(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) };
    if (view === "week") {
      const mon = new Date(d);
      mon.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 7);
      return { from: isoDay(new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() - 1)), to: isoDay(new Date(sun.getFullYear(), sun.getMonth(), sun.getDate() + 1)) };
    }
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    return { from: isoDay(new Date(first.getFullYear(), first.getMonth(), first.getDate() - 1)), to: isoDay(new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1)) };
  }, [cursor, view]);

  const loadAppointments = useCallback(() => {
    setAppointments(null);
    const params = { from: `${range.from}T00:00:00+00:00`, to: `${range.to}T00:00:00+00:00` };
    if (staffFilter) params.staff_id = staffFilter;
    api.get("/appointments", { params }).then((r) => setAppointments(r.data)).catch(() => setAppointments([]));
  }, [range, staffFilter]);

  useEffect(() => {
    if (tab === "calendar") loadAppointments();
  }, [tab, loadAppointments]);

  const refresh = () => {
    loadAppointments();
    setRefreshKey((k) => k + 1);
  };

  const byDay = useMemo(() => {
    const map = {};
    (appointments || []).forEach((a) => {
      const k = dayKey(a.start, tz);
      (map[k] = map[k] || []).push(a);
    });
    Object.values(map).forEach((list) => list.sort((x, y) => x.start.localeCompare(y.start)));
    return map;
  }, [appointments, tz]);

  const overlapIds = useMemo(() => findOverlapIds(appointments), [appointments]);

  const move = (dir) => {
    const d = new Date(cursor);
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCursor(d);
  };

  const heading = useMemo(() => {
    if (view === "day") return fmtDayHeading(cursor);
    if (view === "week") {
      const mon = new Date(cursor);
      mon.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      return `${fmtDayHeading(mon)} — ${fmtDayHeading(sun)}`;
    }
    return cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  }, [cursor, view]);

  const openDetail = (a) => setModal({ type: "detail", appt: a });

  return (
    <div data-testid="appointments-page">
      <PageHeader title="Appointments" sub="Book and manage appointments with your own staff, services and availability. Times are shown in your business timezone.">
        <Btn data-testid="create-appointment-button" onClick={() => setModal({ type: "book" })}>
          <Plus size={15} /> Create Appointment
        </Btn>
      </PageHeader>

      <div className="mb-6 flex flex-wrap gap-1.5 rounded-2xl bg-neutral-100 p-1.5" role="tablist" data-testid="appointments-tabs">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} data-testid={`appt-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`rounded-xl px-3.5 py-2 text-xs font-semibold transition-all sm:text-sm ${tab === t.id ? "bg-white text-[#111111] shadow-sm" : "text-neutral-500 hover:text-[#111111]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "calendar" && (
        <div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Btn variant="ghost" data-testid="cal-prev" onClick={() => move(-1)}><ChevronLeft size={15} /></Btn>
              <Btn variant="ghost" data-testid="cal-today" onClick={() => setCursor(new Date())}>Today</Btn>
              <Btn variant="ghost" data-testid="cal-next" onClick={() => move(1)}><ChevronRight size={15} /></Btn>
              <p className="ml-2 text-sm font-bold text-[#111111]" data-testid="cal-heading">{heading}</p>
            </div>
            <div className="flex items-center gap-2">
              <select data-testid="staff-filter" className={`${inputCls} !w-auto`} value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)}>
                <option value="">All staff</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}{s.active ? "" : " (inactive)"}</option>)}
              </select>
              <div className="flex gap-1 rounded-xl bg-neutral-100 p-1">
                {["day", "week", "month"].map((v) => (
                  <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold capitalize ${view === v ? "bg-white shadow-sm" : "text-neutral-500"}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {appointments === null ? (
            <p className="py-10 text-center text-sm text-neutral-400">Loading…</p>
          ) : view === "month" ? (
            <MonthGrid cursor={cursor} byDay={byDay} tz={tz} overlapIds={overlapIds} onDay={(d) => { setCursor(d); setView("day"); }} onAppt={openDetail} />
          ) : (
            <DayWeekGrid view={view} cursor={cursor} byDay={byDay} tz={tz} overlapIds={overlapIds} onAppt={openDetail} />
          )}
        </div>
      )}

      {tab === "list" && <AppointmentList tz={tz} staffFilter={staffFilter} setStaffFilter={setStaffFilter} staff={staff} onOpen={openDetail} key={`${searchParams.get("lead") || "all"}-${refreshKey}`} refreshKey={refreshKey} leadFilter={searchParams.get("lead") || ""} />}
      {tab === "staff" && <StaffPanel staff={staff} onChanged={loadCore} />}
      {tab === "services" && <ServicesPanel services={services} staff={staff} onChanged={loadCore} />}
      {tab === "settings" && <BookingSettingsPanel settings={settings} onChanged={loadCore} />}

      {modal?.type === "book" && (
        <BookingModal tz={tz} staff={staff} services={services} leads={leads} onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />
      )}
      {modal?.type === "detail" && (
        <DetailModal tz={tz} appt={modal.appt} leads={leads} onClose={() => setModal(null)}
          onChanged={refresh}
          onReschedule={(doc) => setModal({ type: "reschedule", appt: doc })}
          onEdit={(doc) => setModal({ type: "edit", appt: doc })} />
      )}
      {modal?.type === "reschedule" && (
        <RescheduleModal tz={tz} appt={modal.appt} staff={staff} services={services}
          onClose={() => setModal({ type: "detail", appt: modal.appt })}
          onSaved={(updated) => { refresh(); setModal({ type: "detail", appt: updated }); }} />
      )}
      {modal?.type === "edit" && (
        <EditModal appt={modal.appt} leads={leads}
          onClose={() => setModal({ type: "detail", appt: modal.appt })}
          onSaved={(updated) => { refresh(); setModal({ type: "detail", appt: updated }); }} />
      )}
    </div>
  );
}

function MonthGrid({ cursor, byDay, tz, overlapIds, onDay, onAppt }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  const todayKey = isoDay(new Date());
  return (
    <div className="grid grid-cols-7 gap-1.5" data-testid="calendar-month">
      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
        <p key={d} className="pb-1 text-center text-[10px] font-bold uppercase tracking-wider text-neutral-400">{d}</p>
      ))}
      {cells.map((d, i) => {
        if (!d) return <div key={`x${i}`} />;
        const k = isoDay(d);
        const list = byDay[k] || [];
        return (
          <button key={k} data-testid={`cal-day-${k}`} onClick={() => onDay(d)}
            className={`min-h-20 rounded-xl border p-1.5 text-left align-top transition-colors hover:border-[#111111] ${k === todayKey ? "border-[#111111]" : "border-neutral-200"} bg-white`}>
            <span className={`text-[11px] font-bold ${k === todayKey ? "text-[#111111]" : "text-neutral-500"}`}>{d.getDate()}</span>
            <span className="mt-1 block space-y-1">
              {list.slice(0, 2).map((a) => <ApptChip key={a.id} a={a} tz={tz} conflict={overlapIds?.has(a.id)} onClick={(e) => { e.stopPropagation(); onAppt(a); }} />)}
              {list.length > 2 && <span className="block px-1 text-[10px] font-semibold text-neutral-400">+{list.length - 2} more</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function DayWeekGrid({ view, cursor, byDay, tz, overlapIds, onAppt }) {
  const days = [];
  if (view === "day") {
    days.push(new Date(cursor));
  } else {
    const mon = new Date(cursor);
    mon.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));
    for (let i = 0; i < 7; i++) {
      const d = new Date(mon);
      d.setDate(mon.getDate() + i);
      days.push(d);
    }
  }
  const todayKey = isoDay(new Date());
  return (
    <div className={`grid gap-3 ${view === "day" ? "" : "grid-cols-2 md:grid-cols-4 xl:grid-cols-7"}`} data-testid={`calendar-${view}`}>
      {days.map((d) => {
        const k = isoDay(d);
        const list = byDay[k] || [];
        return (
          <Card key={k} className={`p-3 ${k === todayKey ? "border-[#111111]" : ""}`} data-testid={`cal-col-${k}`}>
            <p className="mb-2 border-b border-neutral-100 pb-2 text-xs font-bold text-[#111111]">{fmtDayHeading(d)}</p>
            {list.length === 0 ? (
              <p className="py-3 text-center text-[11px] text-neutral-300">—</p>
            ) : (
              <div className="space-y-1.5">
                {list.map((a) => <ApptChip key={a.id} a={a} tz={tz} conflict={overlapIds?.has(a.id)} onClick={() => onAppt(a)} />)}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function AppointmentList({ tz, staff, staffFilter, setStaffFilter, onOpen, leadFilter, refreshKey }) {
  const [items, setItems] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    const params = {};
    if (staffFilter) params.staff_id = staffFilter;
    if (statusFilter) params.status = statusFilter;
    if (leadFilter) params.lead_id = leadFilter;
    api.get("/appointments", { params }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [staffFilter, statusFilter, leadFilter, refreshKey]);

  const overlapIds = useMemo(() => findOverlapIds(items), [items]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <select data-testid="list-staff-filter" className={`${inputCls} !w-auto`} value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)}>
          <option value="">All staff</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}{s.active ? "" : " (inactive)"}</option>)}
        </select>
        <select data-testid="list-status-filter" className={`${inputCls} !w-auto`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {["Confirmed", "Cancelled", "Completed", "No-show"].map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      {items === null ? (
        <p className="py-10 text-center text-sm text-neutral-400">Loading…</p>
      ) : items.length === 0 ? (
        <Empty testid="appointments-empty" icon={CalendarDays} title="No appointments" body="Create your first appointment — pick a service, a qualified staff member and an available time." />
      ) : (
        <Card className="overflow-x-auto" data-testid="appointments-table">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-xs uppercase tracking-wider text-neutral-500">
                <th className="py-2.5 pr-4 font-semibold">Customer</th>
                <th className="py-2.5 pr-4 font-semibold">Service</th>
                <th className="py-2.5 pr-4 font-semibold">Staff</th>
                <th className="py-2.5 pr-4 font-semibold">Date & time</th>
                <th className="py-2.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`appt-row-${a.id}`} onClick={() => onOpen(a)} className="cursor-pointer border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
                  <td className="py-2.5 pr-4 font-medium text-[#111111]">{a.customer_name}</td>
                  <td className="py-2.5 pr-4 text-neutral-600">{a.service_name}</td>
                  <td className="py-2.5 pr-4 text-neutral-600">{a.staff_name}</td>
                  <td className="py-2.5 pr-4 text-neutral-600">{dayKey(a.start, tz)} · {fmtTime(a.start, tz)}</td>
                  <td className="py-2.5">
                    <Pill tone={STATUS_TONE[a.status] || "grey"}>{a.status}</Pill>
                    {overlapIds.has(a.id) && <span className="ml-1.5 text-[10px] font-bold text-red-600" data-testid={`appt-conflict-${a.id}`}>OVERLAP</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
